const PROGRESS_MARKS = [25, 50, 75]

/**
 * @param {{ track: (name: string, parameters: Record<string, string|number>) => void }} analytics
 */
export function createVideoAnalyticsDirective(analytics) {
  if (!analytics || typeof analytics.track !== 'function') throw new Error('Video analytics tracker is required')
  const stateByElement = new WeakMap()

  function mounted(element, binding) {
    const state = videoState(binding.value)
    const trackStart = () => {
      if (state.started) return
      state.started = true
      analytics.track('vg_video_start', eventParameters(state.metadata))
    }
    const onPlay = () => trackStart()
    const onTimeUpdate = () => {
      if (!Number.isFinite(element.duration) || element.duration <= 0) return
      const percent = (element.currentTime / element.duration) * 100
      for (const mark of PROGRESS_MARKS) {
        if (percent < mark || state.progress.has(mark)) continue
        trackStart()
        state.progress.add(mark)
        analytics.track('vg_video_progress', { ...eventParameters(state.metadata), video_percent: mark })
      }
    }
    const onEnded = () => {
      if (state.completed) return
      trackStart()
      state.completed = true
      analytics.track('vg_video_complete', eventParameters(state.metadata))
    }
    const onError = () => {
      analytics.track('vg_video_error', {
        video_id: state.metadata.video_id,
        placement: state.metadata.placement,
        error_code: mediaErrorCode(element.error?.code),
      })
    }
    state.listeners = { onPlay, onTimeUpdate, onEnded, onError }
    stateByElement.set(element, state)
    element.addEventListener('play', onPlay)
    element.addEventListener('timeupdate', onTimeUpdate)
    element.addEventListener('ended', onEnded)
    element.addEventListener('error', onError)
    if (!element.paused) trackStart()
  }

  function updated(element, binding) {
    const state = stateByElement.get(element)
    if (!state) throw new Error('Video analytics directive updated before mount')
    if (state.metadata.video_id === binding.value.video_id) return
    state.metadata = videoMetadata(binding.value)
    state.started = false
    state.completed = false
    state.progress.clear()
  }

  function unmounted(element) {
    const state = stateByElement.get(element)
    if (!state) return
    element.removeEventListener('play', state.listeners.onPlay)
    element.removeEventListener('timeupdate', state.listeners.onTimeUpdate)
    element.removeEventListener('ended', state.listeners.onEnded)
    element.removeEventListener('error', state.listeners.onError)
    stateByElement.delete(element)
  }

  return Object.freeze({ mounted, updated, unmounted })
}

function videoState(value) {
  return {
    metadata: videoMetadata(value),
    started: false,
    completed: false,
    progress: new Set(),
    listeners: null,
  }
}

function videoMetadata(value) {
  if (!value || typeof value !== 'object') throw new Error('Video analytics metadata is required')
  for (const key of ['video_id', 'video_type', 'placement']) {
    if (typeof value[key] !== 'string' || value[key] === '') throw new Error(`Video analytics ${key} is required`)
  }
  if (value.scenario_id !== undefined && (typeof value.scenario_id !== 'string' || value.scenario_id === '')) {
    throw new Error(`Invalid video analytics scenario_id: ${String(value.scenario_id)}`)
  }
  return Object.freeze({ ...value })
}

function eventParameters(metadata) {
  return metadata.scenario_id === undefined
    ? {
        video_id: metadata.video_id,
        video_type: metadata.video_type,
        placement: metadata.placement,
      }
    : {
        video_id: metadata.video_id,
        video_type: metadata.video_type,
        placement: metadata.placement,
        scenario_id: metadata.scenario_id,
      }
}

function mediaErrorCode(code) {
  switch (code) {
    case 1: return 'aborted'
    case 2: return 'network'
    case 3: return 'decode'
    case 4: return 'unsupported_source'
    default: return 'unknown'
  }
}
